import React from "react";
import { Box, Card, CardContent, Typography, Chip, Paper, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Notifications as NotificationsIcon,
  Group as GroupIcon,
  Event as EventIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  School as SchoolIcon,
  AutoAwesome as SparklesIcon,
} from "@mui/icons-material";
import Logo from "../components/Logo";

const Brochure: React.FC = () => {
  const theme = useTheme();
  const features = [
    {
      icon: <NotificationsIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "Smart Notice Management",
      description:
        "Create, schedule, and manage notices with our intuitive wizard interface",
      tag: "Core Feature",
    },
    {
      icon: <GroupIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "User Management",
      description:
        "Invite, manage, and control access for your entire school community",
      tag: "Admin",
    },
    {
      icon: <EventIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "Event Calendar",
      description:
        "Visual calendar with event management and scheduling capabilities",
      tag: "Events",
    },
    {
      icon: <SecurityIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "Approval Workflow",
      description:
        "Multi-level approval system ensuring content quality and compliance",
      tag: "Security",
    },
    {
      icon: <SpeedIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "Real-time Updates",
      description: "Instant notifications and live updates across all devices",
      tag: "Performance",
    },
    {
      icon: <AnalyticsIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "Dashboard Analytics",
      description:
        "Comprehensive insights into notice engagement and user activity",
      tag: "Analytics",
    },
    {
      icon: <SchoolIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "School Branding",
      description:
        "Custom backgrounds and branded event notices for your institution",
      tag: "Branding",
    },
    {
      icon: <SparklesIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      title: "AI-Powered Quality",
      description:
        "Smart content analysis and quality scoring for better engagement",
      tag: "AI",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "white",
        p: 2,
      }}
    >
      <Card
        elevation={24}
        sx={{
          maxWidth: 800,
          width: "100%",
          borderRadius: 4,
          overflow: "hidden",
          background: alpha(theme.palette.common.white, 0.95),
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Header with Logo */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            p: 4,
            textAlign: "center",
            color: theme.palette.common.white,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Logo width="80px" height="80px" fontSize="48px" />
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mb: 1,
              textShadow: `0 2px 4px ${theme.palette.shadows.dark}`,
            }}
          >
            Noticeboard Dashboard
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontWeight: 500,
              textShadow: `0 1px 2px ${theme.palette.shadows.dark}`,
            }}
          >
            The Ultimate School Communication Platform
          </Typography>
        </Box>

        {/* Features Section */}
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 4,
              fontWeight: 600,
              color: "primary.main",
            }}
          >
            ✨ Powerful Features
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {features.map((feature, index) => (
              <Paper
                key={index}
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 25px ${alpha(theme.palette.common.black, 0.15)}`,
                    borderColor: "primary.main",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ mr: 2, mt: 0.5 }}>{feature.icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mb: 2,
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                    <Chip
                      label={feature.tag}
                      size="small"
                      sx={{
                        bgcolor: "primary.main",
                        color: theme.palette.common.white,
                        fontWeight: 500,
                        fontSize: "0.7rem",
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Call to Action */}
          <Box
            sx={{
              textAlign: "center",
              mt: 4,
              p: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[200]} 100%)`,
              border: "2px dashed",
              borderColor: "primary.main",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "primary.main",
              }}
            >
              🚀 Ready to Transform Your School Communication?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Join hundreds of schools already using Noticeboard Dashboard to
              streamline their communication and enhance community engagement.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Brochure;
